import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box as ActiveStep, Box } from '@node-space/storybook-components/dist/Box'
import Loader from '@node-space/storybook-components/dist/Loader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { AccountContactData } from 'components/accountContact/helpers/types'
import { Description } from 'components/Text'
import { VerificationRoutes } from 'constants/verification'
import { VerificationContextData, VerificationContextProvider } from 'contexts/verificationContext'
import { useFinpromContext } from 'hooks/context/useFinpromContext'
import { useVerticalContext } from 'hooks/context/useVerticalContext'
import { useVerificationStepsData } from 'hooks/useVerificationStepsData'
import { useVerificationStepsNavigation } from 'hooks/useVerificationStepsNavigation'
import { useVerificationStepsRequired } from 'hooks/useVerificationStepsRequired'
import { FinpromStep } from 'pages/Finprom/steps/FinpromStep'
import { AccountPreferenceData } from 'types/accounts'
import { DataContext } from 'types/reactQuery'
import { UnsavedChangesModal } from './_components/UnsavedChangesModal'
import { VerificationMenu } from './_components/VerificationMenu'
import { BusinessAddressFormData as BusinessAddressDataV1 } from './BusinessAddress/BusinessAddressV1/helpers/types'
import {
  BusinessAddressData,
  BusinessAddressData as BusinessAddressDataV2,
} from './BusinessAddress/BusinessAddressV2/helpers/types'
import { Review } from './Review/Review'
import { VerificationStep } from './VerificationStep'

type VerificationStepsData = {
  accountPreferences: DataContext<AccountPreferenceData>
  businessAddress: DataContext<BusinessAddressDataV2 | BusinessAddressDataV1>
}

export interface VerificationStepsProps {
  verificationStepsData: VerificationStepsData
  onCompleteSteps: () => void
}

export const VerificationSteps = ({
  verificationStepsData,
  onCompleteSteps,
}: VerificationStepsProps) => {
  const { t } = useTranslation()
  const { setIsIndustryConfirmed } = useVerticalContext()
  const { activeStep: activeFinpromStep, showFinpromPhaseTwo } = useFinpromContext()

  const [accountContactsList, setAccountContactsList] = useState<AccountContactData[]>([])

  const { accountPreferences, businessAddress } = verificationStepsData || {}

  const { stepsDataRequests, isFetchingStepsData, isErrorFetchingStepsData } =
    useVerificationStepsData()
  const { businessVerificationStatus, accountContacts, dynamicFields } = stepsDataRequests || {}

  const { finpromVerificationSteps, verificationSteps, hasCompletedSteps } =
    useVerificationStepsRequired({
      businessVerificationStatus: businessVerificationStatus?.data,
      accountContacts: accountContacts?.data,
      dynamicFields: dynamicFields?.data,
      businessAddress: businessAddress?.data as BusinessAddressData,
    })

  const isLoadingStepsData = useMemo(
    () => accountPreferences?.isFetching || isFetchingStepsData,
    [accountPreferences?.isFetching, isFetchingStepsData]
  )

  const isErrorLoadingStepsData = useMemo(
    () => accountPreferences?.isError || isErrorFetchingStepsData,
    [accountPreferences?.isError, isErrorFetchingStepsData]
  )

  const verificationContextData: VerificationContextData = {
    businessAddress,
    accountContacts: {
      isEnabled: true,
      isFetching: accountContacts?.isFetching,
      isFetchingError: accountContacts?.isError,
      list: accountContactsList,
      setList: setAccountContactsList,
      refetch: accountContacts?.refetch,
    },
    dynamicFields,
  }

  const { activeStep, unsavedChanges, menuActions, navigationContext } =
    useVerificationStepsNavigation({
      verificationSteps,
      isLoadingStepsData,
      refetchBusinessVerificationStatus: businessVerificationStatus?.refetch,
      refetchDynamicDocuments: dynamicFields?.refetch,
    })

  useEffect(() => {
    if (!accountPreferences?.isFetching) {
      setIsIndustryConfirmed(!!accountPreferences?.data?.isIndustryVerticalConfirmed)
    }
  }, [accountPreferences?.isFetching, accountPreferences?.data?.isIndustryVerticalConfirmed])

  useEffect(() => {
    if (!accountContacts?.isFetching) {
      setAccountContactsList(accountContacts?.data)
    }
  }, [accountContacts?.isFetching])

  return (
    <>
      {isLoadingStepsData && !activeStep ? (
        <Box flex centerChildren>
          <Loader size="large" />
        </Box>
      ) : isErrorLoadingStepsData ? (
        <Box flex centerChildren>
          <StatusCard status="error" headline={t('requestErrors.pageErrorIntro')} borderless>
            <Description>
              {t('requestErrors.comeBackLater')}{' '}
              <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
            </Description>
          </StatusCard>
        </Box>
      ) : (
        <VerificationContextProvider
          {...verificationContextData}
          hasCompletedSteps={hasCompletedSteps}
          setActiveRoute={navigationContext?.setActiveStep}
          setUnsavedChanges={navigationContext?.setHasUnsavedChanges}
          submitStep={navigationContext?.onSubmitStep}
        >
          <Box alignItems="stretch" justifyContent="start" width="full" className="md:flex">
            <VerificationMenu
              activeStep={activeStep}
              steps={verificationSteps}
              finpromSteps={finpromVerificationSteps}
              onClick={menuActions?.onClickMenuItem}
              onClickFinpromStep={menuActions?.onClickFinpromMenuItem}
            />

            <ActiveStep
              flexSize="fill"
              paddingX={16}
              paddingY={24}
              className="md:px-12 md:py-12 xl:pl-0 xl:pr-[300px]"
            >
              <Box maxWidth={448} centerBox aria-label="verificationStep">
                {showFinpromPhaseTwo && !!activeFinpromStep ? (
                  <FinpromStep />
                ) : (
                  <Box>
                    {activeStep === VerificationRoutes.REVIEW_AND_FINISH ? (
                      <Review
                        steps={verificationSteps}
                        isLoadingStepsStatus={isLoadingStepsData}
                        onSubmit={onCompleteSteps}
                      />
                    ) : (
                      <VerificationStep activeStep={activeStep} />
                    )}
                  </Box>
                )}
              </Box>
            </ActiveStep>
          </Box>

          <UnsavedChangesModal {...unsavedChanges} />
        </VerificationContextProvider>
      )}
    </>
  )
}
